import { FREQUENCY_TIERS } from "@athena/common/utils/frequencyTiers";
import React from "react";
import { Text } from "@athena/ui";

// Older deployed APIs still return fixed-day buckets; preserve their meaning during rollout.
export const frequencyTierLabelMap: Record<string, string> = {
    VERY_ACTIVE: "Very active (60+ days)",
    REGULAR: "Regular (20–59 days)",
    OCCASIONAL: "Occasional (5–19 days)",
    INFREQUENT: "Infrequent (1–4 days)",
    ...Object.fromEntries(
        FREQUENCY_TIERS.map(({ tier, label }) => [
            tier,
            label,
        ]),
    ),
};

export const frequencyTierDescriptionMap: Record<string, string> = Object.fromEntries(
    FREQUENCY_TIERS.map(({ tier, description }) => [
        tier,
        description,
    ]),
);

export const engagementCategoryLabelMap: Record<string, string> = {
    AWARENESS: "Awareness",
    EDUCATION: "Education",
    COACHING: "Coaching",
    REFERRAL: "Referral",
};

export const platformLabelMap: Record<string, string> = {
    WEB: "Web",
    IOS: "iOS",
    ANDROID: "Android",
    ADMIN: "Admin console",
    EMAIL: "Email campaign",
};

/**
 * The clients we ship. Both platform breakdowns are scoped to these, because the question they
 * are asked to answer is "which of our apps are people using" — and every other value answers a
 * different question:
 *
 *  - `ADMIN` is an operator acting on a learner's behalf, not the learner using anything.
 *  - `EMAIL` is campaign engagement, which is reported properly by the email reach and click
 *    rates in their own section.
 *  - `null` means we do not know, which is not a platform.
 *
 * An allowlist rather than a filter on the three known exclusions: a Platform value added later
 * then has to be considered deliberately instead of silently appearing in a pie chart.
 *
 * The consequence, on purpose: "Platforms - Actions" no longer sums to `totalActions`. The
 * repository still groups every bucket (see countActionsByPlatform) — this is the report
 * choosing a narrower question, not the query under-counting.
 */
const CLIENT_PLATFORMS: ReadonlySet<string> = new Set([
    "WEB",
    "IOS",
    "ANDROID",
]);

export const clientPlatforms = <T extends { platform?: string | null }>(rows: readonly T[]): T[] =>
    rows.filter((row) => row.platform != null && CLIENT_PLATFORMS.has(row.platform));

/** Backend-originated actions — a campaign import, say — carry no client platform. */
export const platformLabel = (platform: string | null | undefined): string =>
    platform ? (platformLabelMap[platform] ?? platform) : "No platform";

export const pct = (value: number): string => `${(value * 100).toFixed(1)}%`;

/**
 * The counts behind a rate, for the card's detail line.
 *
 * Every rate here divides by members as of the window's end, and a member removed since acting is
 * gone from that denominator while their actions remain — so a rate can legitimately read over
 * 100%. Showing both numbers makes that a visible population mismatch rather than an impossible
 * statistic presented with a straight face. Clamping would hide it instead.
 */
export const ofMembers = (count: number, totalMembers: number | undefined): string =>
    totalMembers === undefined ? `${count}` : `${count} of ${totalMembers}`;

export const formatMonthLabel = (month: string): string =>
    new Date(`${month}T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });

export const formatDayLabel = (day: string): string =>
    new Date(`${day}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** Every day of the given calendar month, as "YYYY-MM-DD", so the chart shows the full month even on days with no actions. */
export const daysInMonth = (year: number, month: number): string[] => {
    const dayCount = new Date(year, month + 1, 0).getDate();
    return Array.from(
        { length: dayCount },
        (_, i) => `${year}-${String(month + 1).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`,
    );
};

/**
 * Every day of the frame's month with its action count, zero on a day with none. Only meaningful for
 * a single-month frame, which is the only one the daily chart is drawn for.
 */
export const dailyActions = (byDay: { day: string; total: number }[], from: Date): { day: string; total: number }[] => {
    const totalByDay = new Map(
        byDay.map((row) => [
            row.day,
            row.total,
        ]),
    );
    return daysInMonth(from.getFullYear(), from.getMonth()).map((day) => ({ day, total: totalByDay.get(day) ?? 0 }));
};

/**
 * The one denominator for every rate on the stats page and in its report — the whole roster as of
 * the window's end, accounts and outstanding invitations together, from the same query as the
 * numerators. See the comment where StatsPage reads it.
 */
export const rosterTotal = (stats: { totalMembers: number; invitedUsers: number }): number =>
    stats.totalMembers + stats.invitedUsers;

/** Top tags by total views summed across every video carrying that tag. */
export const topVideoTags = (
    summaries: { tags: string[]; views: number }[],
    limit = 3,
): { tag: string; views: number }[] => {
    const viewsByTag = new Map<string, number>();
    for (const summary of summaries) {
        for (const tag of summary.tags) {
            viewsByTag.set(tag, (viewsByTag.get(tag) ?? 0) + summary.views);
        }
    }
    return Array.from(
        viewsByTag,
        ([
            tag,
            views,
        ]) => ({ tag, views }),
    )
        .sort((a, b) => b.views - a.views)
        .slice(0, limit);
};

/** True if at least one row carries a non-zero value — some arrays here are fixed-length and non-empty even with nothing to show (e.g. all-zero frequency tiers). */
export const hasAnyValue = <T,>(rows: T[], getValue: (row: T) => number): boolean =>
    rows.some((row) => getValue(row) > 0);

export const NoChartData: React.FC = () => (
    <Text size="sm" muted>
        No data for this time frame.
    </Text>
);
